import { getBootstrap, getDelta, getPartial, postMutations } from "@/api/sync";
import { ApiError } from "@/lib/api-client";
import { uuidv7 } from "@/lib/ids";
import { LocalDb } from "./local-db";
import { ObjectPool, type StoredRecord } from "./object-pool";
import { getModel, models, SYNC_SCHEMA_VERSION } from "./registry";
import type { ChangeDto, ModelName, RecordOf, SyncRecord, SyncStatus, Transaction } from "./types";

const MAX_BATCH = 50;
const MAX_ATTEMPTS = 5;
const PULL_VISIBLE_MS = 5_000;
const PULL_HIDDEN_MS = 30_000;

type EngineOptions = {
	userId: string;
	workspaceId: string;
	onReject?: (message: string, tx: Transaction, code?: string) => void;
};

type Snapshot = {
	status: SyncStatus;
	/** True once the pool holds usable data (from IndexedDB or a bootstrap). */
	hydrated: boolean;
	pending: number;
	lastSyncedAt: number | null;
};

/**
 * Local-first sync engine (Linear-style):
 * 1. Boot hydrates the object pool from IndexedDB — the UI renders with zero network.
 * 2. First run (or schema bump) bootstraps eager models; lazy models load per project.
 * 3. Mutations apply to the pool instantly, persist to a durable queue, and flush in batches.
 * 4. Delta pulls (poll + focus + cross-tab ping) reconcile everyone else's changes.
 */
export class SyncEngine {
	readonly pool = new ObjectPool(models);
	private db: LocalDb | null = null;
	private cursor = 0;
	private partials = new Set<string>();
	private partialLoads = new Map<string, Promise<void>>();
	private generation = 0;
	private flushing = false;
	private pulling: Promise<void> | null = null;
	private flushTimer: ReturnType<typeof setTimeout> | undefined;
	private pullTimer: ReturnType<typeof setTimeout> | undefined;
	private retryDelay = 1_000;
	private channel: BroadcastChannel | null = null;
	private snapshot: Snapshot = {
		status: "loading",
		hydrated: false,
		pending: 0,
		lastSyncedAt: null,
	};
	private statusListeners = new Set<() => void>();
	private cleanup: (() => void)[] = [];

	constructor(private readonly options: EngineOptions) {}

	get workspaceId() {
		return this.options.workspaceId;
	}

	async start() {
		const generation = ++this.generation;
		const db = await LocalDb.open(this.options.userId, this.options.workspaceId);
		if (generation !== this.generation) return void db.close();
		this.db = db;

		const local = await db.load();
		if (generation !== this.generation) return;
		const compatible =
			local.meta.schemaVersion === SYNC_SCHEMA_VERSION && local.meta.cursor != null;

		if (compatible) {
			this.cursor = local.meta.cursor ?? 0;
			this.partials = new Set(local.meta.partials ?? []);
			this.pool.hydrate(local.records, local.transactions);
			this.patchSnapshot({ status: "ready", hydrated: true });
		} else {
			await db.clear();
			this.patchSnapshot({ status: "bootstrapping" });
			await this.bootstrapWithRetry(generation);
		}
		if (generation !== this.generation) return;

		this.listen();
		void this.pull();
		this.scheduleFlush(0);
	}

	stop() {
		this.generation++;
		clearTimeout(this.flushTimer);
		clearTimeout(this.pullTimer);
		for (const fn of this.cleanup) fn();
		this.cleanup = [];
		this.channel?.close();
		this.channel = null;
		void this.db?.close();
		this.db = null;
	}

	// ── Mutations ────────────────────────────────────────────────────────────

	create<N extends ModelName>(model: N, input: Partial<RecordOf<N>>): RecordOf<N> {
		const definition = getModel(model);
		if (!definition) throw new Error(`Unknown sync model "${model}"`);
		const { id = uuidv7(), ...data } = input as Partial<RecordOf<N>> & { id?: string };
		const now = new Date().toISOString();
		const defaults = definition.defaults?.({
			userId: this.options.userId,
			workspaceId: this.options.workspaceId,
			now,
		});
		const optimistic = { ...defaults, ...data, id } as SyncRecord;
		this.enqueue({ op: "create", model, modelId: id, data, optimistic });
		return optimistic as RecordOf<N>;
	}

	update<N extends ModelName>(model: N, id: string, patch: Partial<RecordOf<N>>) {
		this.enqueue({ op: "update", model, modelId: id, data: patch as Record<string, unknown> });
	}

	delete<N extends ModelName>(model: N, id: string) {
		this.enqueue({ op: "delete", model, modelId: id });
	}

	private enqueue(input: Pick<Transaction, "op" | "model" | "modelId" | "data" | "optimistic">) {
		const tx: Transaction = { ...input, id: `tx_${uuidv7()}`, createdAt: Date.now(), attempts: 0 };
		this.pool.addPending(tx);
		this.db?.putTransaction(tx);
		this.patchSnapshot();
		this.scheduleFlush(0);
	}

	private scheduleFlush(delay: number) {
		clearTimeout(this.flushTimer);
		this.flushTimer = setTimeout(() => void this.flush(), delay);
	}

	private async flush() {
		if (this.flushing || !this.db) return;
		const batch = this.pool.pendingTransactions().slice(0, MAX_BATCH);
		if (!batch.length) return;
		this.flushing = true;
		const generation = this.generation;
		try {
			const { results } = await postMutations(
				this.options.workspaceId,
				batch.map(({ id, model, op, modelId, data }) => ({ id, model, op, modelId, data })),
			);
			if (generation !== this.generation) return;
			for (const result of results) {
				const tx = batch.find((t) => t.id === result.id);
				if (!tx) continue;
				if (result.status === "ok") {
					this.pool.resolvePending(tx, result.record, result.syncId);
					this.persistRecord(tx.model, tx.modelId);
					this.db?.deleteTransaction(tx.id);
				} else if (result.retryable && tx.attempts + 1 < MAX_ATTEMPTS) {
					tx.attempts++;
					this.db?.putTransaction(tx);
				} else {
					this.pool.rejectPending(tx);
					this.db?.deleteTransaction(tx.id);
					this.options.onReject?.(result.message, tx, result.code);
				}
			}
			this.retryDelay = 1_000;
			this.patchSnapshot({ status: "ready" });
			this.channel?.postMessage("pull");
		} catch (error) {
			if (
				error instanceof ApiError &&
				error.status >= 400 &&
				error.status < 500 &&
				error.status !== 429
			) {
				if (error.status !== 401) {
					for (const tx of batch) {
						this.pool.rejectPending(tx);
						this.db?.deleteTransaction(tx.id);
					}
					this.options.onReject?.(error.message, batch[0], error.code);
				}
			} else {
				this.patchSnapshot({ status: "offline" });
				this.retryDelay = Math.min(this.retryDelay * 2, 30_000);
				this.scheduleFlush(this.retryDelay);
			}
		} finally {
			this.flushing = false;
			this.patchSnapshot();
			if (this.snapshot.status !== "offline" && this.pool.pendingTransactions().length)
				this.scheduleFlush(0);
		}
	}

	// ── Pulling ──────────────────────────────────────────────────────────────

	private async bootstrapWithRetry(generation: number) {
		for (let delay = 1_000; generation === this.generation; delay = Math.min(delay * 2, 30_000)) {
			try {
				const res = await getBootstrap(this.options.workspaceId);
				if (generation !== this.generation) return;
				const records: StoredRecord[] = [];
				for (const [model, rows] of Object.entries(res.models)) {
					if (!getModel(model)) continue;
					for (const data of rows)
						records.push({ model, id: data.id, data, syncId: res.lastSyncId });
				}
				this.pool.hydrate(records, []);
				for (const record of records) this.db?.put(record);
				this.cursor = res.lastSyncId;
				this.partials.clear();
				this.db?.setMeta("schemaVersion", SYNC_SCHEMA_VERSION);
				this.db?.setMeta("cursor", this.cursor);
				this.db?.setMeta("partials", []);
				this.patchSnapshot({ status: "ready", hydrated: true, lastSyncedAt: Date.now() });
				return;
			} catch (error) {
				if (error instanceof ApiError && error.status === 401) return;
				this.patchSnapshot({ status: "offline" });
				await new Promise((resolve) => setTimeout(resolve, delay));
			}
		}
	}

	/** Pull every change after the cursor. Concurrent callers share one in-flight pull. */
	pull(): Promise<void> {
		if (!this.pulling) {
			this.pulling = this.pullLoop().finally(() => {
				this.pulling = null;
				this.schedulePull();
			});
		}
		return this.pulling;
	}

	private async pullLoop() {
		const generation = this.generation;
		try {
			let hasMore = true;
			while (hasMore && generation === this.generation) {
				const res = await getDelta(this.options.workspaceId, this.cursor);
				if (generation !== this.generation) return;
				for (const change of res.changes) this.applyChange(change);
				this.cursor = res.lastSyncId;
				this.db?.setMeta("cursor", this.cursor);
				hasMore = res.hasMore;
			}
			if (this.snapshot.status === "offline") this.scheduleFlush(0);
			this.patchSnapshot({ status: "ready", lastSyncedAt: Date.now() });
		} catch (error) {
			if (!(error instanceof ApiError)) this.patchSnapshot({ status: "offline" });
		}
	}

	private applyChange(change: ChangeDto) {
		if (!getModel(change.model)) return;
		const applied =
			change.action === "delete" || !change.data
				? this.pool.removeServer(change.model, change.modelId, change.syncId)
				: this.pool.applyServer(change.model, change.data, change.syncId);
		if (applied) this.persistRecord(change.model, change.modelId);
	}

	private persistRecord(model: string, id: string) {
		const record = this.pool.getConfirmed(model, id);
		if (record) this.db?.put(record);
		else this.db?.remove(model, id);
	}

	/** Loads a lazy model's rows for one project once; later changes arrive via delta. */
	ensurePartial(model: ModelName, projectId: string): Promise<void> {
		const key = `${model}:${projectId}`;
		if (this.partials.has(key) || getModel(model)?.load === "eager") return Promise.resolve();
		let load = this.partialLoads.get(key);
		if (!load) {
			load = getPartial(this.options.workspaceId, model, projectId)
				.then((res) => {
					for (const data of res.records) {
						if (this.pool.applyServer(model, data, res.lastSyncId))
							this.persistRecord(model, data.id);
					}
					this.partials.add(key);
					this.db?.setMeta("partials", [...this.partials]);
				})
				.finally(() => this.partialLoads.delete(key));
			this.partialLoads.set(key, load);
		}
		return load;
	}

	isPartialLoaded(model: ModelName, projectId: string) {
		return getModel(model)?.load === "eager" || this.partials.has(`${model}:${projectId}`);
	}

	// ── Status ───────────────────────────────────────────────────────────────

	getSnapshot = () => this.snapshot;

	subscribeStatus = (listener: () => void) => {
		this.statusListeners.add(listener);
		return () => this.statusListeners.delete(listener);
	};

	private patchSnapshot(patch: Partial<Snapshot> = {}) {
		const prev = this.snapshot;
		const next = { ...prev, ...patch, pending: this.pool.pendingTransactions().length };
		if (
			next.status === prev.status &&
			next.hydrated === prev.hydrated &&
			next.pending === prev.pending &&
			next.lastSyncedAt === prev.lastSyncedAt
		) {
			return;
		}
		this.snapshot = next;
		for (const listener of this.statusListeners) listener();
	}

	private schedulePull() {
		clearTimeout(this.pullTimer);
		if (!this.db) return;
		const delay = document.visibilityState === "visible" ? PULL_VISIBLE_MS : PULL_HIDDEN_MS;
		this.pullTimer = setTimeout(() => void this.pull(), delay);
	}

	private listen() {
		const onWake = () => {
			if (document.visibilityState === "visible") void this.pull();
		};
		const onOnline = () => {
			void this.pull();
			this.scheduleFlush(0);
		};
		document.addEventListener("visibilitychange", onWake);
		window.addEventListener("focus", onWake);
		window.addEventListener("online", onOnline);
		this.cleanup.push(
			() => document.removeEventListener("visibilitychange", onWake),
			() => window.removeEventListener("focus", onWake),
			() => window.removeEventListener("online", onOnline),
		);
		this.channel = new BroadcastChannel(`gridcast-sync:${this.options.workspaceId}`);
		this.channel.onmessage = () => void this.pull();
	}
}
